/**
 * Canonical role + status labels for the PRM SPA.
 *
 * Mirrors `apps/api/src/prm_api/auth/role_labels.py`. The two files must
 * stay in lock-step — `tests/unit/test_role_labels.py` on the backend and
 * `tests/lib/role-labels.test.ts` here both pin the canonical strings.
 *
 * 5 conceptual roles (BDM, PO, CM, RM, RA), 4 lifecycle statuses
 * (Pending, Active, Rejected, Deactivated). Roles and statuses are
 * orthogonal — a user has both. "Pending" is a status, not a role.
 *
 * Phase-2 reality (slice 4c): the schema split has landed. `users.status`
 * is a real column, `users.is_resetdata_master` discriminates RM vs RA,
 * and `users.role` is nullable for pending users. `deriveStatus` is now
 * a passthrough — callers feed the column value in directly.
 */

import type { Role } from "@/api/client";

// ---------------------------------------------------------------------------
// Conceptual roles (what the user IS)
// ---------------------------------------------------------------------------

export type ConceptualRole = "bdm" | "po" | "cm" | "rm" | "ra";

export const FULL_LABEL: Record<ConceptualRole, string> = {
  bdm: "Channel Partner BDM",
  po: "Channel Partner Program Owner",
  cm: "Channel Manager",
  rm: "ResetData Master",
  ra: "ResetData Admin",
};

export const SHORT_LABEL: Record<ConceptualRole, string> = {
  bdm: "BDM",
  po: "PO",
  cm: "CM",
  rm: "RM",
  ra: "RA",
};

// CHP-507: one-line explainer shown under the role title on the set-password
// page so an invitee understands what their assigned role can do. Partial by
// design — copy currently exists only for PO and BDM (product decision
// 2026-06-24); invited CM/RM/RA show the title with no blurb until copy lands.
export const ROLE_BLURB: Partial<Record<ConceptualRole, string>> = {
  po: "Full access to your organisation's data and programme management, alongside your individual deal registrations and performance metrics.",
  bdm: "Direct access to your individual deal registrations and performance metrics to track your progress.",
};

export interface DeriveRoleInput {
  role: Role | null | undefined;
  /** From PartnerProfile / CurrentUser. Null defaults to PO for back-compat. */
  is_org_admin?: boolean | null;
  /** Reserved for slice 4c. Null today → all admins default to RM. */
  is_resetdata_master?: boolean | null;
}

export function deriveConceptualRole({
  role,
  is_org_admin,
  is_resetdata_master,
}: DeriveRoleInput): ConceptualRole | null {
  if (!role) return null;
  if (role === "channel_partner") {
    // is_org_admin === false marks a BDM; null/true default to PO
    // (back-compat for users created before slice 4 added the column).
    return is_org_admin === false ? "bdm" : "po";
  }
  if (role === "channel_manager") return "cm";
  if (role === "admin") {
    // Slice 4a: all admins default to RM until 4c adds the
    // is_resetdata_master column. RA = is_resetdata_master === false.
    return is_resetdata_master === false ? "ra" : "rm";
  }
  // "pending" or any unknown role → no conceptual role; caller falls
  // back to the status-based UI (e.g., "Pending Approval" page).
  return null;
}

/**
 * Inverse of {@link deriveConceptualRole}: maps one of the 5 conceptual
 * roles back to the DB fields that would derive to it.
 *
 * Returned fields are `null` when not meaningful for the target role —
 * `is_org_admin` only matters for `channel_partner`, `is_resetdata_master`
 * only for `admin`. Callers wire these into the PATCH /admin/users/{id}
 * conceptual_role flow (slice 4e).
 */
export function dbFieldsForConceptualRole(conceptual: ConceptualRole): {
  role: Role;
  is_org_admin: boolean | null;
  is_resetdata_master: boolean | null;
} {
  switch (conceptual) {
    case "bdm":
      return { role: "channel_partner", is_org_admin: false, is_resetdata_master: null };
    case "po":
      return { role: "channel_partner", is_org_admin: true, is_resetdata_master: null };
    case "cm":
      return { role: "channel_manager", is_org_admin: null, is_resetdata_master: null };
    case "rm":
      return { role: "admin", is_org_admin: null, is_resetdata_master: true };
    case "ra":
      return { role: "admin", is_org_admin: null, is_resetdata_master: false };
  }
}

export function roleFullLabel(input: DeriveRoleInput): string | null {
  const c = deriveConceptualRole(input);
  return c ? FULL_LABEL[c] : null;
}

export function roleShortLabel(input: DeriveRoleInput): string | null {
  const c = deriveConceptualRole(input);
  return c ? SHORT_LABEL[c] : null;
}

// ---------------------------------------------------------------------------
// Statuses (what state the user is IN — orthogonal to role)
// ---------------------------------------------------------------------------

export type Status = "pending" | "active" | "rejected" | "deactivated" | "closed";

// CHP-247: display-only refinement of Status. Invited users are stored as
// `active` (so onboarding gates pass) but read as "Invited" until they accept
// the T&Cs. The backend derives this in GET /admin/users; the SPA only ever
// receives it, never sends it.
export type DisplayStatus = Status | "invited";

export const STATUS_LABEL: Record<DisplayStatus, string> = {
  pending: "Pending Approval",
  active: "Active",
  rejected: "Rejected",
  deactivated: "Deactivated",
  // Slice 0018: terminal state set by DELETE /admin/users/{id}. The user's
  // email is released; the row stays for FK integrity. Historical lists
  // render "Bob Smith (closed)" so the audit trail remains readable.
  closed: "Closed",
  // CHP-247: invited-but-not-yet-onboarded (active + tc_accepted_at IS NULL).
  invited: "Invited",
};

export interface DeriveStatusInput {
  status: Status;
  // Slice 4c: kept for one slice to avoid churning every call site at
  // once. Unused — the function trusts `status` directly.
  is_active?: boolean | null;
}

export function deriveStatus({ status }: DeriveStatusInput): Status {
  // Phase-2 passthrough: trust the users.status column. Anyone still
  // calling the phase-1 way (passing role + is_active) gets whatever
  // they put in `status`; the backend CHECK constraint upstream
  // rejects invalid values.
  return status;
}

export function statusLabel(status: DisplayStatus): string {
  return STATUS_LABEL[status];
}

// ---------------------------------------------------------------------------
// Role tokens + UI strings (slice 4d ambient terminology sweep)
// ---------------------------------------------------------------------------
//
// Canonical user-facing strings for everything OUTSIDE direct
// role-identity displays (those already go through roleFullLabel).
// Inline-reference call sites do template interpolation:
//   `Your ${CHANNEL_MANAGER} will review the deal…`
//
// The ESLint `no-restricted-syntax` rule blocks new bare literals like
// "Channel Manager" outside this module — adding a new ambient string
// means adding to one of the dicts below.

export const CHANNEL_PARTNER = "Channel Partner";
export const CHANNEL_MANAGER = "Channel Manager";
export const RESETDATA_MASTER = "ResetData Master";
export const RESETDATA_ADMIN = "ResetData Admin";

// CHP-400: the "Channel Partner Details" block on the review + detail screens.
// `CHANNEL_PARTNER_DETAILS` is the section title; `DEAL_OWNER` relabels the
// former "Lead contact" sub-header (the lead BDM/PO who owns the deal).
export const CHANNEL_PARTNER_DETAILS = "Channel Partner Details";
export const DEAL_OWNER = "Deal Owner";

// Top-nav slot labels keyed by viewer's conceptual role. BDM/PO share
// the partner slot ("Channel Partner"); RM/RA diverge in the admin slot
// ("ResetData Master" vs "ResetData Admin") to surface the slice 4c
// distinction. Full canonical labels per Joe 2026-05-23 — no
// abbreviations in the nav row, even though it makes the row wider, so
// every visible role label aligns to the 5-role vocabulary.
export const NAV_LINK_LABEL: Record<ConceptualRole, string> = {
  bdm: "Channel Partner",
  po: "Channel Partner",
  cm: "Channel Manager",
  rm: "ResetData Master",
  ra: "ResetData Admin",
};

// Dashboard <h1> titles. BDM/PO dashboards use a dynamic greeting
// ("Welcome, {name}.") that doesn't contain a role identifier, so
// they're absent from this map.
export const DASHBOARD_TITLE: Partial<Record<ConceptualRole, string>> = {
  cm: `${CHANNEL_MANAGER} Dashboard`,
  rm: "User Management",
  ra: "User Management",
};

// Invite CTA button labels. Keyed by what's being invited, not by the
// inviter's role. CHP-447: the Admin Hub's two-CTA header uses
// `partner_invite` ("Add Channel Partner") and `admin_invite`
// ("Add New Admin User", title case). `user_invite` is the legacy generic
// label kept for the dialog heading fallback.
export const INVITE_CTA_LABEL = {
  partner_invite: `Add ${CHANNEL_PARTNER}`,
  admin_invite: "Add New Admin User",
  user_invite: "Add User",
} as const;
